import React from 'react';

import clsx from 'clsx';
import * as yup from 'yup';
import {ErrorMessage, Form, Formik} from 'formik';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import {makeStyles} from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import api from '../services/api'
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';

import AppButton from '../components/AppButton';
import InformationList from '../components/InformationList';

import {useDispatch, useSelector} from 'react-redux';

import {addNotification} from '../redux/actions/notifications';
import { SELECTOR_USER } from '../redux/selectors/auth';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundImage: `url(${require('../assets/images/contact.png')})`,
    backgroundSize: 'cover',
    objectFit: 'cover',
    height: '100vh',
    [theme.breakpoints.down('sm')]: {
      height: '100%',
    },
  },
  content: {
    height: '100%',
  },
  text: {
    color: theme.palette.common.white,
  },
  textTitle: {
    fontSize:"3.3rem",
    color: theme.palette.common.white,
  },
  left: {
    backgroundColor: 'rgba(10, 89, 141, 0.8)',
    paddingRight: 40,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      padding: `${theme.spacing(7)}px ${theme.spacing(2)}px`,
    },
  },
  right: {
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
      padding: `${theme.spacing(4)}px ${theme.spacing(2)}px`,
    },
  },
  info: {
    boxShadow: '0px 2px 12px rgba(0, 0, 0, 0.1)',
    transform: 'translateX(-40px)',
    borderRadius: 20,
    maxWidth: 400,
    padding: `${theme.spacing(6)}px ${theme.spacing(3)}px`,
    [theme.breakpoints.down('sm')]: {
      transform: 'translateX(0)',
    },
  },
  form: {
    maxWidth: 500,
  },
  caption: {
    lineHeight: 1.5,
  },
  field: {
    // marginBottom: theme.spacing(1.5),
    '& .MuiInputLabel-root': {
      color: theme.palette.common.white,
      // transform: 'translate(14px, 15px) scale(1)'
    },
    '& .MuiOutlinedInput-root': {
      position: 'relative',
      borderRadius: 10,
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100%',
        height: '100%',
        left: 0,
        top: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.02)',
        backdropFilter: 'blur(10px)',
      },
      '& fieldset': {
        borderColor: 'rgba(255, 255, 255, .6)',
      },
      '& .MuiOutlinedInput-input': {
        padding: `${theme.spacing(1.6)}px ${theme.spacing(1.5)}px`,
        zIndex: 1,
        color: theme.palette.common.white,
      },
      '&:hover fieldset': {
        borderColor: 'rgba(255, 255, 255, 1)',
      },
      '&.Mui-focused fieldset': {
        borderColor: 'rgba(255, 255, 255, 1)',
      },
    },
  },
  error: {
    fontSize: '.8rem',
    height: 19,
    marginTop: 1,
    marginBottom: 2,
    color: theme.palette.secondary.main,
    fontWeight: 400,
  },
}));

const validationSchema = yup.object().shape({
  name: yup.string().required('Nombre es requerido.'),
  email: yup
    .string()
    .email('Debe ser un correo electrónico valid.')
    .required('Correo electrónico es requerido.'),
  text: yup
    .string()
    .max(156, 'Máximo 256 caracteres')
    .required('Mensaje es requerido.'),
  subject: yup
    .string()
    .max(156, 'Máximo 256 caracteres')
    .required('Asunto es requerido.'),
});

const Contact = () => {
  const classes = useStyles();
  const [loading, setLoading] = React.useState(false);
  const [initialValues, setValues] = React.useState({
    email: '',
    name: '',
    text: '',
    subject: '',
  });
  const dispatch = useDispatch();
  const user = useSelector(SELECTOR_USER)

  const submit = async ({name, text, email, subject}) => {
    setLoading(true);
    try {
      await api.contact.create({
        full_name: name,
        email,
        ...(user ? { user_id: user.id } : {}),
        message: text,
        subject,
      });
      setValues({
        email: '',
        name: '',
        text: '',
        subject: '',
      });
      dispatch(addNotification('Mensaje enviado con exito !', 'success'));
    } catch (error) {
      dispatch(addNotification(error.message, 'error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={classes.root}>
      <Grid container className={classes.content}>
        <Grid item xs={12} md={7} className={classes.left}>
          <div className={classes.form}>
            <Box mb={2} clone>
              <Typography className={classes.textTitle} variant="h2">
                Contáctanos
              </Typography>
            </Box>
            <Typography
              className={clsx(classes.text, classes.caption)}
              variant="body1"
            >
              Haznos saber en qué podemos ayudarte y te escribiremos en el menor
              tiempo posible o comunícate a través de la línea de atención.
            </Typography>
            <Formik
              enableReinitialize={true}
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={submit}
            >
              {({handleSubmit, values}) => (
                <Form
                  onKeyPress={(event) => {
                    event.key === 'Enter' && handleSubmit();
                  }}
                >
                  <Box display="flex" mt={4} flexDirection="column">
                    <TextField
                      id="filled-name-input"
                      className={classes.field}
                      value={values.name}
                      onChange={(e) =>
                        setValues({...initialValues, name: e.target.value})
                      }
                      label="Nombre"
                      name="name"
                      variant="outlined"
                    />
                    <Box mb={1} className={classes.error}>
                      <ErrorMessage name="name" />
                    </Box>
                    <TextField
                      className={classes.field}
                      value={values.subject}
                      onChange={(e) =>
                        setValues({...initialValues, subject: e.target.value})
                      }
                      label="Asunto"
                      name="subject"
                      variant="outlined"
                    />
                    <Box mb={1} className={classes.error}>
                      <ErrorMessage name="subject" />
                    </Box>
                    <TextField
                      className={classes.field}
                      value={values.email}
                      onChange={(e) =>
                        setValues({...initialValues, email: e.target.value})
                      }
                      label="Correo electrónico"
                      type="email"
                      name="email"
                      variant="outlined"
                    />
                    <Box mb={1} className={classes.error}>
                      <ErrorMessage name="email" />
                    </Box>
                    <TextField
                      className={classes.field}
                      label="Mensaje"
                      name="text"
                      value={values.text}
                      onChange={(e) =>
                        setValues({...initialValues, text: e.target.value})
                      }
                      multiline
                      rows={4}
                      variant="outlined"
                    />
                    <Box mb={1} className={classes.error}>
                      <ErrorMessage name="text" />
                    </Box>
                  </Box>
                  <Box mt={3} clone>
                    <AppButton
                      loading={loading}
                      onClick={handleSubmit}
                      round
                      variant="contained"
                      color="default"
                    >
                      <Typography color="primary" variant="button">
                        Enviar
                      </Typography>
                    </AppButton>
                  </Box>
                </Form>
              )}
            </Formik>
          </div>
        </Grid>
        <Grid item xs={12} md={5} className={classes.right}>
          <Card className={classes.info}>
            <CardContent>
              <Grid container>
                <InformationList primary column />
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
};

export default Contact;
